import { Box, SvgIcon, Typography, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { InboxRounded, LanguageRounded, SearchRounded, Instagram, Facebook, Twitter, YouTube, OpenInNewRounded, WarningRounded } from "@mui/icons-material";
import { useGetAllPublishingsQuery, useGetContentModelQuery, useSearchContentQuery } from "../../../../../../../../../shell/services/instance";
import { useGetUsersQuery } from "../../../../../../../../../shell/services/accounts";
import { startCase } from "lodash";
import { BRAND_COLORS } from "utility/brandColors";
import { useSelector } from "react-redux";
import { formatLocalized } from "shell/i18n/dates";
import { parseISO, isValid as isValidDate } from "date-fns";
const SOURCE_DETAIL_MAP = {
  "(direct)": {
    color: "info.dark",
    bgcolor: "blue.100",
    icon: InboxRounded
  },
  google: {
    color: "error.dark",
    bgcolor: "red.100",
    icon: SearchRounded
  },
  bing: {
    color: "error.dark",
    bgcolor: "red.100",
    icon: SearchRounded
  },
  instagram: {
    color: BRAND_COLORS.instagram,
    bgcolor: "purple.100",
    icon: Instagram
  },
  facebook: {
    color: BRAND_COLORS.facebook,
    bgcolor: "blue.100",
    icon: Facebook
  },
  twitter: {
    color: BRAND_COLORS.twitter,
    bgcolor: "blue.100",
    icon: Twitter
  },
  youtube: {
    color: BRAND_COLORS.youtube,
    bgcolor: "red.100",
    icon: YouTube
  }
} as const;
const fmtShort = (d?: string) => {
  if (!d) return "";
  const parsed = parseISO(d);
  const dateObj = isValidDate(parsed) ? parsed : new Date(d);
  return isValidDate(dateObj) ? formatLocalized(dateObj, "MMM d") : "";
};
export const NameCell = ({
  path,
  screenPageViews,
  topSource,
  topSourceValue,
  externalLink
}: {
  path?: string;
  screenPageViews?: number;
  topSource: keyof typeof SOURCE_DETAIL_MAP;
  topSourceValue: number;
  externalLink?: string;
}) => {
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const {
    data: item,
    isFetching: isItemFetching
  } = useSearchContentQuery({
    query: path,
    limit: 1
  });
  const foundItem = item?.[0]?.web?.path === path ? item?.[0] : null;
  const {
    data: publishings,
    isFetching: isPublishingFetching
  } = useGetAllPublishingsQuery();
  const foundPublishing = publishings?.find(p => p.itemZUID === foundItem?.meta?.ZUID);
  const {
    data: users,
    isFetching: isUsersFetching
  } = useGetUsersQuery();
  const foundUser = users?.find(u => u.ZUID === foundPublishing?.publishedByUserZUID);
  const {
    data: model,
    isFetching: isModelsFetching
  } = useGetContentModelQuery(foundItem?.meta?.contentModelZUID, {
    skip: !foundItem?.meta?.contentModelZUID
  });
  const languages = useSelector((state: any) => state.languages);
  const langCode = languages.find((lang: any) => lang.ID === foundItem?.meta?.langID)?.code;
  const langDisplay = langCode ? `(${langCode}) ` : "";
  const hasSiblings = foundItem?.siblings && Object.keys(foundItem?.siblings).length > 1;
  const isFetching = isItemFetching || isPublishingFetching || isUsersFetching || isModelsFetching;
  const getImage = () => {
    const ogImage = Object.keys(foundItem?.data || {}).find(v => v === "ogimage" || v === "ogImage" || v === "og_image" || v === "og:image");
    if (ogImage) return foundItem?.data?.[ogImage];
    return Object.values(foundItem?.data || {}).find(v => typeof v === "string" && v.startsWith("3-"));
  };
  if (isFetching || !path) {
    return <Box display="flex" height="40px" width="100%" alignItems="center" gap={1}>
        <Skeleton variant="rectangular" width="40px" height="40px" sx={{
        minWidth: "40px"
      }} />
        <Box width="100%">
          <Skeleton variant="rectangular" width="100%" height="12px" sx={{
          bgcolor: "grey.200",
          mb: 1
        }} />
          <Skeleton variant="rectangular" width="80%" height="12px" />
        </Box>
      </Box>;
  }
  return <Box display="flex" height="40px" width="100%" alignItems="center" gap={1} onClick={() => {
    if (foundItem) {
      history.push(`/content/${foundItem?.meta?.contentModelZUID}/${foundItem?.meta?.ZUID}`);
    } else if (externalLink) {
      window.open(externalLink);
    }
  }} data-sentry-element="Box" data-sentry-component="NameCell" data-sentry-source-file="NameCell.tsx">
      <Box height="40px" width="40px" bgcolor="info.main" borderRadius="4px" data-sentry-element="Box" data-sentry-source-file="NameCell.tsx">
        {Object.values(foundItem?.data || {}).some(v => typeof v === "string" && v.startsWith("3-")) && <img width="100%" height="100%" style={{
        objectFit: "cover",
        minWidth: "40px",
        borderRadius: "4px"
      }} src={`${CONFIG.SERVICE_MEDIA_RESOLVER}/resolve/${getImage()}/getimage/?w=200&h=200&type=fit`} onError={e => {
        e.currentTarget.style.display = "none";
      }} />}
      </Box>

      <Box data-sentry-element="Box" data-sentry-source-file="NameCell.tsx">
        <Box display="flex" gap={0.5} alignItems="center" data-sentry-element="Box" data-sentry-source-file="NameCell.tsx">
          <Typography variant="body2" fontWeight={600} mb={0.25} noWrap maxWidth="420px" data-sentry-element="Typography" data-sentry-source-file="NameCell.tsx">
            {hasSiblings ? langDisplay : ""}
            {foundItem?.web?.metaTitle || foundItem?.web?.metaLinkText || path}
          </Typography>
          <OpenInNewRounded onClick={e => {
          e.stopPropagation();
          if (externalLink) window.open(externalLink);
        }} color="action" sx={{
          width: 16,
          height: 16
        }} data-sentry-element="OpenInNewRounded" data-sentry-source-file="NameCell.tsx" />
        </Box>

        {foundItem ? <Box display="flex" gap={1.5}>
            <Typography variant="body3" fontWeight={600} color="text.secondary">
              {fmtShort(foundPublishing?.publishAt)}
            </Typography>
            <Typography variant="body3" fontWeight={600} color="text.secondary">
              {foundUser ? `${foundUser.firstName} ${foundUser.lastName}` : t("shell.unknownUser")}
            </Typography>
            <Typography variant="body3" fontWeight={600} color="text.secondary">
              {model?.label}
            </Typography>

            {topSource && <Box display="flex" alignItems="center">
                <Box display="flex" alignItems="center" gap={0.25} px={0.5} py={0.25} bgcolor={SOURCE_DETAIL_MAP[topSource]?.bgcolor || "yellow.50"} sx={{
            borderRadius: "2px 0 0 2px"
          }}>
                  <SvgIcon component={SOURCE_DETAIL_MAP[topSource]?.icon || LanguageRounded} sx={{
              width: 8,
              height: 8,
              color: SOURCE_DETAIL_MAP[topSource]?.color || "warning.main"
            }} />
                  <Typography fontSize="10px" fontWeight={600} lineHeight="12px" color={SOURCE_DETAIL_MAP[topSource]?.color || "warning.main"}>
                    {startCase(topSource)}
                  </Typography>
                </Box>
                <Box px={0.5} py={0.25} bgcolor={SOURCE_DETAIL_MAP[topSource]?.color || "warning.main"} sx={{
            borderRadius: "0 2px 2px 0"
          }}>
                  <Typography fontSize="8px" fontWeight={600} lineHeight="12px" color="common.white">
                    {isNaN(Math.floor(topSourceValue / (screenPageViews || 0) * 100)) ? 0 : Math.floor(topSourceValue / (screenPageViews || 1) * 100)}
                    %
                  </Typography>
                </Box>
              </Box>}
          </Box> : <Box display="flex" gap={0.5} alignItems="center">
            <WarningRounded sx={{
          width: 12,
          height: 12
        }} color="warning" />
            <Typography variant="body3" fontWeight={600} color="text.secondary">
              {t("content.analyticsItemNotExist")}
            </Typography>
          </Box>}
      </Box>
    </Box>;
};